import moment from "moment";

import { InternalPatientResponse } from "@/Common/Api/ExternalCommunications/Dto/Responses/InternalPatientResponse";
import { LabActionTaken, MyleActionDTO, MyleNoteDTO, ObservationDTO, RequestDTO } from "@/Common/Api/Labs/Dto/Responses/LabMessageGroupDTO";
import { AttachedCommunicationsPopup } from "@/Common/Components/AttachedCommunications/AttachedCommunicationsPopup";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { HyperLinkControl } from "@/Common/Components/Buttons/HyperLinkControl";
import { FixedClickableTextPanel } from "@/Common/Components/ClickableText/FixedClickableTextPanel";
import { FeatureFlagControl } from "@/Common/Components/FeatureFlagControl";
import { CheckboxControl } from "@/Common/Components/Inputs/CheckboxControl";
import { KendoComboBox } from "@/Common/Components/Inputs/KendoComboBox";
import { TextAreaResizable } from "@/Common/Components/Inputs/TextAreaResizable";
import { LinkPatientToLabPopup } from "@/Common/Components/Labs/LinkPatientToLabPopup";
import { LinkingPopupService } from "@/Common/Components/Modals/LinkingPopup/LinkingPopupService";
import { CreatePatientPopup } from "@/Common/Components/Patient/CreatePatientPopup";
import { PortalStatusButton } from "@/Common/Components/Portal/PortalStatusIcon";
import { ReferalDoctorSearchAutocompleteCustom } from "@/Common/Components/Search/ReferalDoctorSearchAutocompleteCustom";
import { EmployeeTitleHelper } from "@/Common/Helpers/EmployeeTitleHelper";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { NameHelper, NameHelperFormat } from "@/Common/Helpers/NameHelper";
import { PortalHelper } from "@/Common/Helpers/PortalHelper";
import { SecondScreenHelper } from "@/Common/Helpers/SecondScreenHelper";
import { AttachedCommunication } from "@/Common/Models/AttachedCommunication/AttachedCommunication";
import { AttachedCommunicationType } from "@/Common/Models/AttachedCommunication/AttachedCommunicationType";
import { Patient } from "@/Common/Models/Patient/Patient";
import { IPatientBannerModel } from "@/Common/Models/Patient/UI/IPatientBannerModel";
import { IPatientCreationModel } from "@/Common/Models/Patient/UI/IPatientCreationModel";
import { Person } from "@/Common/Models/Person/Person";
import { SecondScreenNoteData } from "@/Common/Models/SecondScreenNoteData";
import { LabInboxes, MessageOrTaskContext, RedirectTo } from "@/Common/Models/UI/Enums";
import { PersonDropdownItem } from "@/Common/Models/UI/Inputs/PersonDropdownItem";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { SecondScreenNoteService } from "@/Common/Services/SecondScreenNoteService";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { RouterHistory } from "@/Common/Utilities/Router/RouterHistory";
import { ExternalMessageFormPopupComponent } from "@/Communications/Components/ExternalCommunications/Form/ExternalMessageFormPopupComponent";
import { DirectoryProfessionalSearchLockingComponent } from "@/ContactDirectory/Components/DirectoryProfessionalSearchLockingComponent";
import { DirectoryHealthCareProvider } from "@/ContactDirectory/Models/DirectoryHealthCareProvider";
import { LabMessage } from "@/Labs/Models/LabMessage";
import { LabMessageTableItem } from "@/Labs/Models/LabMessageTableItem";
import { ILabModel } from "@/Labs/Models/UI/ILabModel";
import { ILabsInboxModel } from "@/Labs/Models/UI/ILabsInboxModel";
import { ILabsTabModel } from "@/Labs/Models/UI/ILabsTabModel";
import { ISideNoteModel } from "@/Labs/Models/UI/ISideNoteModel";
import { SendResultStrategy } from "@/Labs/Services/Portal/Strategies/SendResultStrategy";
import { MedicalSummaryPanelAction } from "@/Patient/Actions/MedicalSummaryPanelAction";
import { MedicalSummaryPanel } from "@/Patient/Components/MedicalSummaryPanel/MedicalSummaryPanel";
import { MedicalSummaryPanelContext } from "@/Patient/Components/MedicalSummaryPanel/MedicalSummaryPanelEnums";
import { SummaryPanelButtons } from "@/Patient/Components/MedicalSummaryPanel/SummaryPanelButtons";
import { SendToPortalPopup } from "@/Portal/Components/SendToPortalPopup";
import { LabsHelper } from "../Helpers/LabsHelper";
import { LabViewBelowPatientBannerRegistryView } from "./Extensions/LabViewBelowPatientBannerRegistry";
import { PatientBannerForLabs } from "./PatientBannerForLabs";

import styles from "./LabView.module.scss";

import img_icon_labchart from "@/Globals/_images/icons/LabChart.png";

interface ILabViewProps {
    Model: ILabModel;
    InboxModel: ILabsInboxModel;
    SideNoteModel: ISideNoteModel;
    LabTabModel: ILabsTabModel
}

class LabViewState {
    public isMedicalSummaryPanelOpen: boolean;
    public isMedicalSummaryLoaded: boolean;
}

class LabView extends BaseReactComponent<ILabViewProps, LabViewState> {
    private myleHelper: MyleHelper = new MyleHelper();
    private myleInfoPatient: Patient = null;
    private LabFillerID: string = null;
    private labContent: string = "labs-leftContent";
    private shouldShowPortalButton: boolean = false;

    private consultationRef: React.RefObject<HTMLDivElement> = React.createRef();
    private followUpAndPlanRef: React.RefObject<HTMLDivElement> = React.createRef();

    constructor(props: ILabViewProps) {
        super(props);

        const initialState = new LabViewState();
        initialState.isMedicalSummaryPanelOpen = !!cacheStore?.UserSettings.LabViewMedicalSummaryPanelSettings?.IsMedicalSummaryPanelOpen;
        this.state = initialState;
    }

    public componentDidMount(): void {
        $(window).trigger("resize"); // Resize for when people switch tab and come back

        const labfillerId = this.props.Model.get("LabMessage").Model.LabFillerID;
        const cleanLabFillerId = this.getlabFillerId(labfillerId);
        sessionStore.lastFocusedClickableTextController = "lab_note_Laboratoire" + cleanLabFillerId;

        let reference = $("#" + this.labContent);
        let nativeReference = reference.get(0);

        if (labsStore.labViewScrollPosition >= 0) {
            window.setTimeout(() => {
                this.adjustScroll(labsStore.labViewScrollPosition);
            }, 200);
        }

        reference.on("scroll", () => {
            labsStore.labViewScrollPosition = nativeReference.scrollTop; // quick upadates to component private member
        });

        this.updateSecondScreen();
    }

    public componentDidUpdate(prevProps: ILabViewProps): void {
        $(window).trigger("resize");

        // ensure we scroll sections back to top when changing lab
        if (this.props.Model !== null && this.props.Model.get("LabMessage") != null && this.props.Model.get("LabMessage").Model != null &&
            prevProps.Model !== null && prevProps.Model.get("LabMessage") != null && prevProps.Model.get("LabMessage").Model != null) {
            if (this.props.Model.get("LabMessage").Model.LabFillerID !== prevProps.Model.get("LabMessage").Model.LabFillerID) {
                window.setTimeout(() => {
                    $("#labs-leftContent").scrollTop(0);
                }, 0);

                window.setTimeout(() => {
                    $("#control_div_lab").scrollTop(0);
                }, 100);
            }
        }

        this.updateSecondScreen();
    }

    public shouldComponentUpdate(nextProps: ILabViewProps, nextState: LabViewState): boolean {
        const modelRefChanged = this.props.Model !== nextProps.Model;

        if (modelRefChanged && this.props.Model.get("LabMessage").Model.LabFillerID != nextProps.Model.get("LabMessage").Model.LabFillerID) {
            const labfillerId = nextProps.Model.get("LabMessage").Model.LabFillerID;
            const cleanLabFillerId = this.getlabFillerId(labfillerId);
            sessionStore.lastFocusedClickableTextController = "lab_note_Laboratoire" + cleanLabFillerId;
        }

        return (
            modelRefChanged
            || this.props.SideNoteModel !== nextProps.SideNoteModel
            || this.props.LabTabModel !== nextProps.LabTabModel
            || this.props.InboxModel !== nextProps.InboxModel
            || this.state !== nextState
        );
    }

    public componentWillUnmount(): void {
        this.updateSecondScreen(true);
    }

    public render(): JSX.Element {
        const canFileAndHold = sessionStore.canApproveDocument();

        const labMessage = this.props.Model.get("LabMessage");
        this.myleInfoPatient = this.props.Model.get("MylePatient");

        this.LabFillerID = labMessage.Model.LabFillerID;
        const IsExpandedNote = canFileAndHold && this.props.Model.get("IsNoteExpanded");

        const selectedPatient = this.props.Model.get("SelectedPersonForLinkingLab");
        const selectedPatinetForLinkHasFullInfo = selectedPatient?.Patient != null;

        const patientDDN = labMessage.Model.Patient.DateOfBirth != null
            ? moment(labMessage.Model.Patient.DateOfBirth).format("YYYY-MM-DD") + " "
            : "";

        this.shouldShowPortalButton =
            cacheStore.ClinicSettings_Portal.EnablePortalWebsite
            && (this.myleInfoPatient?.IsRegisteredToPortal() || cacheStore.ClinicSettings.EnablePortalInvitationFlowV2)
            && this.props.Model.get("LabMessage").Model.HasEncapsulatedData;

        const bannerModel = {
            Model: null,
            PatientDropdownVisible: false
        };

        const showAssociationControl = this.myleInfoPatient == null || !this.state.isMedicalSummaryLoaded;

        let m: IPatientBannerModel = Immutable.fromJS(bannerModel);
        if (this.myleInfoPatient != null)
            m = m.set("Model", this.myleInfoPatient);

        const defaultLinkPatientInputText = LabsHelper.GetDefaultPatientNameForLinkPatient(patientDDN, labMessage.Model.Patient.FirstName, labMessage.Model.Patient.LastName);

        return (

            <div className="workspace">
                {this.renderHeaderForLabsView(showAssociationControl, labMessage, m)}
                {this.props.Model.get("ShowExternalCommunicationPopup") &&
                    this.renderExternalCommunicationPopup(this.myleInfoPatient)
                }

                {this.props.InboxModel.get("IsSendToPatientPortalPopupOpen") && (
                    <SendToPortalPopup
                        model={this.props.InboxModel.get("PortalLinkedItemModel")}
                        strategy={new SendResultStrategy()}
                    />
                )}

                {this.props.Model.get("AttachedCommunicationsPopupOpen") && (
                    <AttachedCommunicationsPopup
                        controlId="attached_communications_labview"
                        context={AttachedCommunicationType.Task}
                        leftPosition={this.props.Model.get("AttachedCommunicationsPopupX")}
                        topPosition={this.props.Model.get("AttachedCommunicationsPopupY")}
                        handleRedirect={ac => this.onAttachedCommunicationClick(ac)}
                        handleClose={() => this.onAttachedCommunicationClose()}
                        attachedCommunications={this.props.Model.get("IndividualTaskLinkedItems")}
                    />
                )}
                <div className="fxheight">
                    <div className="main-content myle-form labs">
                        <div className="labs-view">

                            {this.renderLeftSideOfPage(labMessage)}
                            {!this.isManualLabInbox() && (
                                <>
                                    {IsExpandedNote && this.myleInfoPatient != null && (
                                        <div className="tabcontrol" onClick={() => this.collapseLeftMenu()}>
                                            <div className="slide-menu-icon blue">
                                                <span />
                                                <span />
                                                <span />
                                            </div>
                                        </div>
                                    )}
                                    {!(IsExpandedNote && this.myleInfoPatient) && (
                                        <div className="tabcontrol" onClick={() => this.expandLeftMenu()}>
                                            <div className="slide-menu-icon blue">
                                                <span />
                                                <span />
                                                <span />
                                            </div>
                                        </div>
                                    )}

                                    {this.renderRightSideOfPage(IsExpandedNote, labMessage)}
                                </>
                            )}
                        </div>
                    </div>
                </div>

                {this.renderFooterForLabsView(IsExpandedNote, labMessage, canFileAndHold)}

                {this.props.Model.get("ShowLinkPatientPopup") && (
                    <LinkPatientToLabPopup
                        defaultText={defaultLinkPatientInputText}
                        isLinkButtonDisable={!selectedPatinetForLinkHasFullInfo}
                        onLinkPatient={() => sessionStore.dispatch(LocalOperations.LabsUI.LinkSelectedPatient)}
                        onCancel={() => sessionStore.dispatch(LocalOperations.LabsUI.ToggleLinkPatientPopup)}
                        onToggleCreatePatientPopupClick={() => this.toggleCreatePatientPopupClick()}
                        linkPatientPopupOptions={this.props.Model.get("LinkPatientPopupOptions")}
                    />
                )}
                {this.props.Model.get("ShowCreatePatientPopup") && this.renderCreatePatientPopup()}
            </div>
        );
    }

    private renderExternalCommunicationPopup(myleInfoPatient: Patient): JSX.Element {
        const selectedPatient = Person.FromPatient(myleInfoPatient);

        return (
            <ExternalMessageFormPopupComponent
                selectedPatient={InternalPatientResponse.fromPerson(selectedPatient)}
                selectedAttachments={this.props.Model.get("LabMessage") && this.props.Model.get("LabMessage").convertEncapsulatedDataToExportablePDF()}
                onClose={() => this.showExternalCommunicationPopup(false)}
                onSend={() => this.showExternalCommunicationPopup(false)}
            />
        );
    }

    private adjustScroll(scrollPos: number): void {
        const reference = $("#" + this.labContent);
        const nativeReference = reference.get(0);
        if (nativeReference != null)
            nativeReference.scrollTop = scrollPos;
    }

    private renderHeaderForLabsView(showAssociationControl: boolean, labMessage: LabMessage, m: IPatientBannerModel): JSX.Element {
        return (
            <div className="myle-page-header bottom-border">

                {showAssociationControl === false && (
                    <div>
                        <PatientBannerForLabs
                            Model={m}
                            shouldLoadLastUpcomingAppointment
                        />
                        <div>
                            <span>
                                <a onClick={() => this.onNewTaskClick()}>
                                    {cacheStore.GetString("NewTaskLabel")}
                                </a>
                                {this.props.Model.get("IndividualTaskLinkedItems").any() && (
                                    <span
                                        style={{ marginLeft: "5px", cursor: "pointer" }}
                                        onClick={e => this.onTaskTrailClick(e)}
                                        className={this.props.Model.get("AreAllTasksCompleted")
                                            ? "circle-green"
                                            : "circle-orange"
                                        }
                                    >
                                        {this.props.Model.get("IndividualTaskLinkedItems").length}
                                    </span>
                                )}
                            </span>
                            {cacheStore.ClinicSettings.EnableExternalCommunication && (
                                <div style={{ display: "inline" }}>
                                    <span>{" / "}</span>
                                    <span>
                                        <a onClick={() => this.showExternalCommunicationPopup()}>
                                            {cacheStore.GetString("NewExternalCommunicationLabel")}
                                        </a>
                                    </span>
                                </div>
                            )}
                            <span>{" / "}</span>
                            <HyperLinkControl
                                operation={LocalOperations.LabsUI.ToggleLinkPatientPopup}
                                text={cacheStore.GetString("RelinkPatientLabel")}
                            />
                            <LabViewBelowPatientBannerRegistryView patient={this.props.Model.get("MylePatient")} />
                        </div>
                    </div>
                )}
                {showAssociationControl && (
                    <div className="row">
                        <div className="col-lg-12 col-md-12 col-sm-12">
                            <div className="tb pateint-header">
                                <div className="tbc r1">
                                    <h4>{NameHelper.formatFullNameForHasName(labMessage.Model.Patient, NameHelperFormat.FirstNameFirst)}</h4>
                                    <ul className="no-margin">
                                        <li><strong>{sessionStore.getDefaultHealthCardLabel()}</strong> <span>{labMessage.Model.Patient.NAM}</span></li>
                                        <li><strong>{cacheStore.GetString("SexAtBirthLabel")}</strong> <span>{labMessage.Model.Patient.Sex}</span></li>
                                        <li><strong>{cacheStore.GetString("DateofBirthLabel")} </strong> <span>{moment(labMessage.Model.Patient.DateOfBirth).format("YYYY/MM/DD")}</span></li>
                                        {!this.isManualLabInbox() && (
                                            <li> <HyperLinkControl operation={LocalOperations.LabsUI.ToggleLinkPatientPopup} text={cacheStore.GetString("LinkLabel")} /></li>
                                        )}
                                    </ul>
                                </div>
                                <div className="tbc r2">
                                    <ul className="no-margin">
                                        {labMessage.Model.Patient?.Phones?.map(phone => (
                                            <li key={phone.Number}>
                                                <span>{phone.Number}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        );
    }

    private renderLeftSideOfPage(labMessage: LabMessage): JSX.Element {
        const cssForDetailsArrow = this.props.Model.get("ShowAdditionalDetails") === true
            ? "up"
            : "down";
        const cssForAbnormalAndCriticalOberservationArrow = this.props.Model.get("ShowAbnormalAndCriticalObservations") === true
            ? "up"
            : "down";

        const lab = this.props.Model.get("LabMessage");

        let maxPrelevement = labMessage.Model.Order.RequestsList.sortByPropertyDesc(x => x.MaxObservationDatetime_CollectedDatetime).firstOrDefault()?.MaxObservationDatetime_CollectedDatetime;

        let observationRange = toDateRange(labMessage.Model.FirstObservation, labMessage.Model.LastObservation);
        let prelevmentDateTime = toDateRange(labMessage.Model.PrelevmentDateTime ?? maxPrelevement, maxPrelevement);
        let medfarReceptionRange = toDateRange(labMessage.Model.FirstInsertInARLMYLEDateTime, labMessage.Model.LastInsertInARLMYLEDateTime);

        const requestNumber = labMessage.Model.IsManualLabEntry ? labMessage.Model.RequestNumber : labMessage.Model.LabFillerID;

        return (
            <div className="left" id="labs-leftContent">
                <div className="vertical-scroll-only" id={this.labContent} key={this.labContent}>
                    <div className={styles.labHeaderContainer}>
                        {cacheStore.ClinicSettings.EnableResultsFixes && lab.Model.Detail && (
                            <div className={styles.labDetailsContainer}>
                                <span title={lab.Model.Detail}>
                                    {lab.Model.Detail}
                                </span>
                            </div>
                        )}
                        <div>
                            {this.formatLabHeaderStatus(labMessage.Model.ActionTakenByUnknowSplitted)}
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-lg-12 col-md-12 col-sm-12">
                            <div className="myle-toggle-btn" onClick={() => this.toggleAdditionalInfo()}>
                                <i className={cssForDetailsArrow} />
                                <span>{cacheStore.GetString("AdditionalinformationLabel")}</span>
                                <div className="pull-right" onClick={e => e.stopPropagation()}>
                                    {this.formatEncapsolatedData(labMessage)}
                                </div>
                            </div>
                        </div>
                    </div>

                    {this.props.Model.get("ShowAdditionalDetails") && (
                        <div className="row" style={{ margin: "0 0 0 10px" }}>
                            <div className="col-lg-5 col-md-5 col-sm-5 text-left">
                                <table className="basic-table labs">
                                    <tbody>
                                        {this.getColumn(0, cacheStore.GetString("OriginLabel"), labMessage.Model.SendingFacility)}
                                        {this.getColumn(1, cacheStore.GetString("ContextLabel"), labMessage.Model.Context)}
                                        {this.getColumn(2, cacheStore.GetString("RequestsLabel") + " No.#", requestNumber)}
                                        {this.getColumn(3, cacheStore.GetString("EmissionLabel"), !IsNullOrWhiteSpace(labMessage.Model.EmittedDateTime) ? moment(labMessage.Model.EmittedDateTime).format("YYYY/MM/DD HH:mm:ss A") : "--")}
                                        {this.getColumn(4, cacheStore.GetString("MyleReceptionDate"), medfarReceptionRange)}
                                        {this.getColumn(5, cacheStore.GetString("RequestedByLabel"), labMessage.getFormattedPrescriptor())}
                                        <tr key={6}>
                                            <td className="c1">
                                                <span>C.C</span>
                                            </td>
                                            <td className="c2">
                                                {labMessage.getFormattedCC().map(name => (
                                                    <div key={name}><strong>{name}</strong></div>
                                                ))}
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>

                            <div className="col-lg-7 col-md-7 col-sm-7">
                                <table className="basic-table labs">
                                    <tbody>
                                        {this.getColumn(0, cacheStore.GetString("CollectionDateLabel"), prelevmentDateTime)}
                                        {this.getColumn(1, cacheStore.GetString("SpecimenReceivedDateLabel"), (labMessage.Model.SpecimenReceptionDateTime && labMessage.Model.SpecimenReceptionDateTime != "") ? moment(labMessage.Model.SpecimenReceptionDateTime).format("YYYY/MM/DD HH:mm:ss A") : "--")}
                                        {this.getColumn(2, cacheStore.GetString("ObservationsLabel"), observationRange)}
                                    </tbody>
                                </table>
                            </div>
                            <div className="row" />
                        </div>
                    )}

                    <div className="row">
                        <div className="col-lg-12 col-md-12 col-sm-12">
                            <div className="myle-toggle-btn" onClick={() => this.ToggleAbnormalAndCriticalInfo()}>
                                <i className={cssForAbnormalAndCriticalOberservationArrow} />
                                <span>{cacheStore.GetString("AbnormalObservationsMLabel")}</span>
                            </div>
                        </div>
                    </div>

                    {this.props.Model.get("ShowAbnormalAndCriticalObservations") && (
                        <div className="row">
                            <div className="col-lg-12">
                                <table className="table lab-view-tb">
                                    <thead>
                                        <tr className="noborder">
                                            <th>{cacheStore.GetString("AnalyseLabel")}</th>
                                            <th>{cacheStore.GetString("ResultsLabel")}</th>
                                            <th>{cacheStore.GetString("UnitsLabel")}</th>
                                            <th>{cacheStore.GetString("ReferenceShortlbl")}</th>
                                            <th>{cacheStore.GetString("FlagMLabel")}</th>
                                            <th>{cacheStore.GetString("GraphiqueShortlbl")}</th>
                                            <th style={{ width: "80px" }}>{cacheStore.GetString("NotesLabel") + "/" + cacheStore.GetString("ActionsLabel")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {this.formatLabResults(labMessage.Model.Order.RequestsList, true)}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    <div className="row">
                        <div className="col-lg-12 col-md-12 col-sm-12">
                            <div className="myle-toggle-btn">
                                <div className="flex-container">
                                    <div><span>{cacheStore.GetString("FullResultsMLabel")}</span></div>
                                    <div className="flex-container" style={{ justifyContent: "flex-start", marginRight: "25px" }}>
                                        <div style={{ marginRight: "4px" }}>
                                            {cacheStore.GetString(cacheStore.ClinicSettings.EnableResultsFixes ? "ResultDetailsLabel" : "NotesDLabel")}
                                        </div>
                                        {lab.Model.ParentLevel_MyleNoteDtos.length == 0 && (
                                            <div>
                                                <a
                                                    onClick={e =>
                                                        cacheStore.ClinicSettings.EnableResultsFixes ?
                                                            this.globalResultDetailClick(this.LabFillerID, lab.Model.ParentLevel_MyleNoteDtos, lab.Model.Detail, e) :
                                                            this.globalNotesClick(this.LabFillerID, lab.Model.ParentLevel_MyleNoteDtos, e)
                                                    }
                                                    className="notes_dropdown_main pointer"
                                                >[<span className="red-text"> + </span>]
                                                </a>
                                            </div>
                                        )}
                                        {lab.Model.ParentLevel_MyleNoteDtos.length > 0 && (
                                            <div>
                                                <a
                                                    onClick={e =>
                                                        cacheStore.ClinicSettings.EnableResultsFixes ?
                                                            this.globalResultDetailClick(this.LabFillerID, lab.Model.ParentLevel_MyleNoteDtos, lab.Model.Detail, e) :
                                                            this.globalNotesClick(this.LabFillerID, lab.Model.ParentLevel_MyleNoteDtos, e)
                                                    }
                                                    className="notes_dropdown_main pointer"
                                                >[<strong className="red-text"> {lab.Model.ParentLevel_MyleNoteDtos.length} </strong>]
                                                </a>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-lg-12">
                            {labMessage.Model.Patient.NotesList?.map((patientNote, index) => (
                                patientNote.Comment && (
                                    <div key={`${patientNote.Comment} ${index}`}>
                                        <span className="paddedx2" style={{ fontFamily: "monospace" }}>{patientNote.Comment.replace(/\n\n\n\n/g, "\n").replace(/\n\n/g, "\n")}</span>
                                    </div>
                                )
                            ))}
                            <table className="table lab-view-tb">
                                <thead>
                                    <tr className="noborder">
                                        <th>{cacheStore.GetString("AnalyseLabel")}</th>
                                        <th>{cacheStore.GetString("ResultsLabel")}</th>
                                        <th>{cacheStore.GetString("UnitsLabel")}</th>
                                        <th>{cacheStore.GetString("ReferenceShortlbl")}</th>
                                        <th>{cacheStore.GetString("FlagMLabel")}</th>
                                        <th>{cacheStore.GetString("GraphiqueShortlbl")}</th>
                                        <th style={{ width: "80px" }}>{cacheStore.GetString("NotesLabel") + "/" + cacheStore.GetString("ActionsLabel")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {this.formatLabResults(labMessage.Model.Order.RequestsList, false)}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private renderRightSideOfPage(IsExpandedNote: boolean, labMessage: LabMessage): JSX.Element {
        const cleanLabFillerId = this.getlabFillerId(labMessage.Model.LabFillerID);
        return (

            <div className={IsExpandedNote && this.myleInfoPatient != null ? "transition right" : "transition collapsed right"}>
                <div className={`row ${styles.marginBottom}`}>
                    <div className="col-lg-12 col-md-12 col-sm-12">
                        <div>{sessionStore.getCurrentEmployee().nameWithPrefix() + " "}
                            <HyperLinkControl text={"(" + (labsStore.Model.get("CurrentSpeciality") ? labsStore.Model.get("CurrentSpeciality").GetValue() : "") + ")"} operation={LocalOperations.LabsUI.SiteNote_SpecialityToggle} />
                            {cacheStore.ClinicSettings.EnableSecondScreen && (
                                <SummaryPanelButtons
                                    isMedicalSummaryPanelOpen={this.state.isMedicalSummaryPanelOpen}
                                    onMedicalSummaryLinkClick={() => this.toggleMedicalSummaryPanel()}
                                    onPopoutClick={() => this.openSecondScreen()}
                                    cssClass="labsToggleContainer"
                                    hideOpenSummaryPanelLink
                                />
                            )}
                        </div>
                        <div className="display-flex-with-space-between">
                            <p>{moment(Date.now()).format("YYYY/MM/DD")}</p>
                        </div>
                    </div>
                </div>
                {cacheStore.ClinicSettings.EnableSecondScreen && this.props.Model.get("MylePatient") && cacheStore.ClinicSettings.EnableSecondScreen && (
                    <>
                        <div className={styles.medicalSummaryLink} onClick={() => this.toggleMedicalSummaryPanel()}>
                            <div className={styles.icon}>
                                <span className={`glyphicon ${this.state.isMedicalSummaryPanelOpen ? "glyphicon-chevron-up" : "glyphicon-chevron-down"}`} />
                            </div>
                            <label>{cacheStore.GetString("MedicalSummaryMLabel")}</label>
                        </div>
                        <div className={`animation-container-in-result ${this.state.isMedicalSummaryPanelOpen ? "open" : ""}`}>
                            <MedicalSummaryPanel
                                patientContactId={this.props.Model.get("MylePatient").IndividualResponse.Contact_id}
                                cssClass="containerLabView"
                                context={MedicalSummaryPanelContext.ResultsTab}
                                onMedicalSummaryLoaded={() => this.mutateState(s => s.isMedicalSummaryLoaded = true)}
                            />
                        </div>
                    </>
                )}
                <div id="control_div_lab" key="control_div_lab">
                    <div className="row">
                        <div className="col-lg-12 col-md-12 col-sm-12">
                            <div className="banner-label">
                                <span>{cacheStore.GetString("EvolutionaryNoteMLabel")}</span>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-lg-12 col-md-12 col-sm-12">
                            <label>{cacheStore.GetString("ReasonLabel")}</label>
                            <TextAreaResizable
                                verticalResizeOnly={true}
                                setCursorStartOnFocus={true}
                                id={"lab_note_reason" + cleanLabFillerId}
                                ref={"lab_note_reason" + cleanLabFillerId}
                                text={this.props.SideNoteModel.get("Raison")}
                                isDisabled={false}
                                minHeightPx={35}
                                operation={LocalOperations.LabsUI.SiteNote_RaisonChange}
                                resetTextOnIdChange={true}
                            />
                            <div>
                                <FixedClickableTextPanel
                                    cssClass="text-helper-container"
                                    context="RFV"
                                    inputElementId={"lab_note_reason" + cleanLabFillerId}
                                    isLinkDisabled={false}
                                    isClickableTextPanelOpen={this.props.Model.get("ReasonClickableTextVisible")}
                                    onClickableTextPanelOpen={(): void => this.showClickableText("reason")}
                                    onClickableTextPanelClose={(): void => this.hideClickableText()}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-lg-12 col-md-12 col-sm-12">
                            <label>{cacheStore.GetString("LaboratoryLabel")}</label>
                            <TextAreaResizable
                                verticalResizeOnly={true}
                                setCursorStartOnFocus={true}
                                id={"lab_note_Laboratoire" + cleanLabFillerId}
                                ref={"lab_note_Laboratoire" + cleanLabFillerId}
                                text={this.props.SideNoteModel.get("Laboratoire")}
                                isDisabled={false}
                                minHeightPx={70}
                                operation={LocalOperations.LabsUI.SiteNote_LaboratoireChange}
                                resetTextOnIdChange={true}
                            />
                            <div>
                                <FixedClickableTextPanel
                                    cssClass="text-helper-container"
                                    context="LAB"
                                    inputElementId={"lab_note_Laboratoire" + cleanLabFillerId}
                                    isClickableTextPanelOpen={this.props.Model.get("LabClickableTextVisible")}
                                    onClickableTextPanelOpen={(): void => this.showClickableText("lab")}
                                    onClickableTextPanelClose={(): void => this.hideClickableText()}
                                    isLinkDisabled={false}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-lg-12 col-md-12 col-sm-12">
                            <label>{cacheStore.GetString("RadiologyLabel")}</label>
                            <TextAreaResizable
                                verticalResizeOnly={true}
                                setCursorStartOnFocus={true}
                                id={"lab_note_Imagerie" + cleanLabFillerId}
                                ref={"lab_note_Imagerie" + cleanLabFillerId}
                                text={this.props.SideNoteModel.get("Imagerie")}
                                isDisabled={false}
                                minHeightPx={70}
                                operation={LocalOperations.LabsUI.SiteNote_ImagerieChange}
                                resetTextOnIdChange={true}
                            />
                            <div>
                                <FixedClickableTextPanel
                                    cssClass="text-helper-container"
                                    context="IMG"
                                    inputElementId={"lab_note_Imagerie" + cleanLabFillerId}
                                    isLinkDisabled={false}
                                    isClickableTextPanelOpen={this.props.Model.get("ImagingClickableTextVisible")}
                                    onClickableTextPanelOpen={(): void => this.showClickableText("imaging")}
                                    onClickableTextPanelClose={(): void => this.hideClickableText()}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="row">
                        <div className="col-lg-12 col-md-12 col-sm-12">
                            <label>{cacheStore.GetString("RequestsLabel")}</label>
                            <CheckboxControl label={cacheStore.GetString("UrgentLabel")} checked={this.props.SideNoteModel.get("UrgentRequete")} operation={LocalOperations.LabsUI.SiteNote_UrgentRequeteChange} DivWrap={true} />
                            <TextAreaResizable
                                verticalResizeOnly={true}
                                setCursorStartOnFocus={true}
                                id={"lab_note_requete" + cleanLabFillerId}
                                ref={"lab_note_requete" + cleanLabFillerId}
                                text={this.props.SideNoteModel.get("Requete")}
                                isDisabled={false}
                                minHeightPx={70}
                                operation={LocalOperations.LabsUI.SiteNote_RequeteChange}
                                resetTextOnIdChange={true}
                                invalid={this.props.SideNoteModel.get("UrgentRequete") && !this.props.SideNoteModel.get("Requete")}
                            />
                            <div>
                                <FixedClickableTextPanel
                                    cssClass="text-helper-container"
                                    context="REQ"
                                    inputElementId={"lab_note_requete" + cleanLabFillerId}
                                    isLinkDisabled={false}
                                    isClickableTextPanelOpen={this.props.Model.get("RequestClickableTextVisible")}
                                    onClickableTextPanelOpen={(): void => this.showClickableText("request")}
                                    onClickableTextPanelClose={(): void => this.hideClickableText()}
                                />
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-lg-12 col-md-12 col-sm-12">
                            <label>{cacheStore.GetString("ConsultationsLabel")}</label>
                            <CheckboxControl
                                label={cacheStore.GetString("UrgentLabel")}
                                checked={this.props.SideNoteModel.get("UrgentConsultation")}
                                operation={LocalOperations.LabsUI.SiteNote_UrgentConsultationChange}
                                DivWrap={true}
                            />
                            <div style={{ margin: "0 0 10px 0" }}>
                                <KendoComboBox
                                    controlId="doc_edit_sources"
                                    selectedItem={this.props.SideNoteModel.get("ConsultationSpeciality") ? this.props.SideNoteModel.get("ConsultationSpeciality") : ""}
                                    placeholder={cacheStore.GetString("SpecialityLabel")}
                                    items={Immutable.List(cacheStore.FindLookupsByTypeCode("Specialization_def").map(x => x.GetValue()))}
                                    changeOperation={LocalOperations.LabsUI.SiteNote_ConsultationSpecialityChange}
                                    invalid={this.isConsultationElementFilled() && !this.props.SideNoteModel.get("ConsultationSpeciality")}
                                />
                            </div>
                            <div style={{ margin: "0 0 10px 0" }}>
                                <FeatureFlagControl
                                    flag={cacheStore.ClinicSettings.EnableContactDirectory}
                                    activeFeature={(
                                        <DirectoryProfessionalSearchLockingComponent
                                            componentId={`add_lab_ConsultationReferal_${cleanLabFillerId}`}
                                            onItemSelected={item => sessionStore.dispatch(
                                                LocalOperations.LabsUI.SiteNote_ConsultationDestinataireSelect,
                                                {
                                                    value: new PersonDropdownItem({ Item: DirectoryHealthCareProvider.toPerson(item.entity as DirectoryHealthCareProvider) })
                                                }
                                            )}
                                            selectedItems={this.props.SideNoteModel
                                                ?.get("ConsultationDestinataire")
                                                ?.map(p => ({ Name: `${p.Name} ${p.Licence ? `(${p.Licence}) ` : ""}` }))
                                                .toArray() ?? []
                                            }
                                            onTextChange={text => sessionStore.dispatch(LocalOperations.LabsUI.SiteNote_ConsultationDestiantaireTextChanged, { value: text })}
                                            searchText={this.props.SideNoteModel.get("ConsultationDestinataireFreeText")}
                                            dataCy="contactDirectory-patientLabView-referralDoctorSearch"
                                            onRemoveItem={() => sessionStore.dispatch(LocalOperations.LabsUI.SiteNote_ConsultationDestinataireClear)}
                                            placeholder={cacheStore.GetString("RecipientLabel")}
                                        />
                                    )}
                                    fallbackFeature={(
                                        <ReferalDoctorSearchAutocompleteCustom
                                            useblurevent={true}
                                            focusOnLoad={false}
                                            disabled={false}
                                            displayList={true}
                                            selectedDoctors={this.props.SideNoteModel.get("ConsultationDestinataire")}
                                            selectionOp={LocalOperations.LabsUI.SiteNote_ConsultationDestinataireSelect}
                                            componentId={"add_lab_ConsultationReferal_" + cleanLabFillerId}
                                            clearOp={LocalOperations.LabsUI.SiteNote_ConsultationDestinataireClear}
                                            closeDropDownAfterSelection={true}
                                            changedTextValueEvent={LocalOperations.LabsUI.SiteNote_ConsultationDestiantaireTextChanged}
                                            resetValueOnDefaultValueChange={true}
                                            placeholder={cacheStore.GetString("RecipientLabel")}
                                        />
                                    )}
                                />
                            </div>
                            <div ref={this.consultationRef}>
                                <TextAreaResizable
                                    verticalResizeOnly={true}
                                    setCursorStartOnFocus={true}
                                    id={"lab_note_consult_" + cleanLabFillerId}
                                    ref={"lab_note_consult_" + cleanLabFillerId}
                                    text={this.props.SideNoteModel.get("Consultation")}
                                    isDisabled={false}
                                    minHeightPx={70}
                                    operation={LocalOperations.LabsUI.SiteNote_ConsultationChange}
                                    resetTextOnIdChange={true}
                                    invalid={this.isConsultationElementFilled() && !this.props.SideNoteModel.get("Consultation")}
                                />
                            </div>
                            <div>
                                <FixedClickableTextPanel
                                    cssClass="text-helper-container"
                                    context="CONS"
                                    inputElementId={"lab_note_consult_" + cleanLabFillerId}
                                    isLinkDisabled={false}
                                    isClickableTextPanelOpen={this.props.Model.get("ConsultClickableTextVisible")}
                                    onClickableTextPanelOpen={(): void => this.showClickableText("consult")}
                                    onClickableTextPanelClose={(): void => this.hideClickableText()}
                                    textboxHeight={this.consultationRef.current?.clientHeight ?? 0}
                                    clickableTextPanelCssStyle={{ bottom: "0px" }}
                                    isAboveLabel
                                />
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className="col-lg-12 col-md-12 col-sm-12">
                            <label>{cacheStore.GetString("OtherPlanNoteLbl")}</label>
                            <div ref={this.followUpAndPlanRef}>
                                <TextAreaResizable
                                    verticalResizeOnly={true}
                                    setCursorStartOnFocus={true}
                                    id={"lab_note_FollowUp" + cleanLabFillerId}
                                    ref={"lab_note_FollowUp" + cleanLabFillerId}
                                    text={this.props.SideNoteModel.get("FollowUp")}
                                    isDisabled={false}
                                    minHeightPx={70}
                                    operation={LocalOperations.LabsUI.SiteNote_FollowUpChange}
                                    resetTextOnIdChange={true}
                                />
                            </div>
                            <div>
                                <FixedClickableTextPanel
                                    cssClass="text-helper-container"
                                    context="OTH"
                                    inputElementId={"lab_note_FollowUp" + cleanLabFillerId}
                                    isLinkDisabled={false}
                                    isClickableTextPanelOpen={this.props.Model.get("FollowUpPlanClickableTextVisible")}
                                    onClickableTextPanelOpen={(): void => this.showClickableText("followupplan")}
                                    onClickableTextPanelClose={(): void => this.hideClickableText()}
                                    textboxHeight={this.followUpAndPlanRef.current?.clientHeight ?? 0}
                                    clickableTextPanelCssStyle={{ bottom: "0px" }}
                                    isAboveLabel
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private isConsultationElementFilled(): boolean {
        return (
            !!this.props.SideNoteModel.get("Consultation")
            || !!this.props.SideNoteModel.get("ConsultationDestinataire")
            || !!this.props.SideNoteModel.get("ConsultationSpeciality")
            || !!this.props.SideNoteModel.get("UrgentConsultation")
        );
    }

    private isConsultationSectionValid(): boolean {
        if (this.isConsultationElementFilled()) {
            return (
                !!this.props.SideNoteModel.get("Consultation")
                && !!this.props.SideNoteModel.get("ConsultationSpeciality")
            );
        }
        return true;
    }

    private isLabRequestSectionValid(): boolean {
        return (
            !this.props.SideNoteModel.get("UrgentRequete")
            || !!this.props.SideNoteModel.get("Requete")
        );
    }

    private renderFooterForLabsView(IsExpandedNote: boolean, labMessage: LabMessage, canFileAndHold: boolean): JSX.Element {
        if (IsExpandedNote && this.myleInfoPatient != null) {
            return this.renderExtendedFooter(labMessage, canFileAndHold);
        }
        else if (!(IsExpandedNote && this.myleInfoPatient != null)) {
            return this.renderLimitedFooter(labMessage, canFileAndHold);
        }
        else return null;
    }

    private renderLimitedFooter(labMessage: LabMessage, canFileAndHold: boolean): JSX.Element {
        let portalSyncButton: JSX.Element = null;

        if (this.shouldShowPortalButton) {
            // todo - get lab emitted date and shared item last share date (not implemented yet) to get if unsynced
            const buttonProps = PortalHelper.getPortalButtonPropsForShareableItem(labMessage);

            portalSyncButton = (
                <PortalStatusButton
                    className="push-right-5"
                    icon="share"
                    style={{ display: "inline-flex" }}
                    onClick={() => this.PortalStatusClicked(labMessage)}
                    {...buttonProps}
                />
            );
        }

        return (
            <div data-ctrlref className="ws-footer flex-container" style={{ "paddingBottom": "6px", alignItems: "flex-end" }}>
                <div className="c1" style={{ paddingTop: "2px" }}>
                    <ButtonControl
                        className="btn btn-primary push-right-5"
                        onClick={() => sessionStore.dispatch(LocalOperations.LabsUI.PreviousLab)}
                        disabled={labsStore.LabFillerIDsViewedNaviguationPosition == 0}
                        label="<"
                    />
                    <ButtonControl
                        className="btn btn-primary push-right-5"
                        onClick={() => sessionStore.dispatch(LocalOperations.LabsUI.NextLab)}
                        label=">"
                    />
                </div>
                {canFileAndHold && (
                    <div className="c2">
                        {(!labMessage.Model.IsManualLabEntry) && (
                            <ButtonControl
                                disabled={this.props.Model.get("IsSaving")}
                                className="btn btn-default push-right-5"
                                id={labMessage.Model.LabFillerID}
                                onClick={() => sessionStore.dispatch(LocalOperations.LabsUI.HoldLab, { id: labMessage.Model.LabFillerID })}
                                label={cacheStore.GetString("OnHoldLabAction")}
                            />
                        )}
                        <FeatureFlagControl
                            activeFeature={(
                                EmployeeTitleHelper.isDoctor(sessionStore.myleEmployeeTitle) && (
                                    <ButtonControl
                                        disabled={this.props.Model.get("IsSaving")}
                                        variant="brand"
                                        id={labMessage.Model.LabFillerID}
                                        onClick={() => sessionStore.dispatch(LocalOperations.LabsUI.FileManualLab, { id: labMessage.Model.LabFillerID })}
                                        label={cacheStore.GetString("ClassifyLabel")}
                                    />
                                )
                            )}
                            fallbackFeature={(
                                <ButtonControl
                                    disabled={this.props.Model.get("IsSaving")}
                                    className="btn btn-primary"
                                    id={labMessage.Model.LabFillerID}
                                    onClick={() => sessionStore.dispatch(LocalOperations.LabsUI.FileLab, { id: labMessage.Model.LabFillerID })}
                                    label={cacheStore.GetString("ClassifyLabel")}
                                />
                            )}
                            flag={cacheStore.ClinicSettings.EnableManualEntryOfLabResults && labMessage.Model.IsManualLabEntry}
                        />
                        {portalSyncButton}
                    </div>
                )}
                {!canFileAndHold && (
                    <div className="c2">
                        {portalSyncButton}
                    </div>
                )}
            </div>
        );
    }

    private renderExtendedFooter(labMessage: LabMessage, canFileAndHold: boolean): JSX.Element {
        let portalSyncButton: JSX.Element = null;

        if (this.shouldShowPortalButton) {
            // todo - get lab emitted date and shared item last share date (not implemented yet) to get if unsynced
            const buttonProps = PortalHelper.getPortalButtonPropsForShareableItem(labMessage);

            portalSyncButton = (
                <PortalStatusButton
                    className="push-right-5"
                    icon="share"
                    style={{ display: "inline-flex" }}
                    onClick={() => this.PortalStatusClicked(labMessage)}
                    {...buttonProps}
                />
            );
        }

        const isBottomButtonDisabled = !this.myleInfoPatient || this.props.Model.get("IsSaving") || !this.isConsultationSectionValid() || !this.isLabRequestSectionValid();

        return (
            <div data-ctrlref className="ws-footer flex-container labs-view" style={{ paddingBottom: "6px" }}>
                <div className="flex-container mid" style={{ alignItems: "flex-end" }}>
                    <div className="c1" style={{ paddingTop: "3px" }}>
                        <ButtonControl
                            className="btn btn-primary push-right-5"
                            onClick={() => sessionStore.dispatch(LocalOperations.LabsUI.PreviousLab)}
                            disabled={labsStore.LabFillerIDsViewedNaviguationPosition == 0}
                            label="<"
                        />
                        <ButtonControl
                            className="btn btn-primary push-right-5"
                            onClick={() => sessionStore.dispatch(LocalOperations.LabsUI.NextLab)}
                            label=">"
                        />
                    </div>
                    {canFileAndHold && (
                        <div className="c2">
                            <div className="flex-container" style={{ alignItems: "flex-end" }}>
                                <ButtonControl
                                    className="btn btn-default push-right-5"
                                    id={labMessage.Model.LabFillerID}
                                    onClick={() => sessionStore.dispatch(LocalOperations.LabsUI.HoldLab, { id: labMessage.Model.LabFillerID })}
                                    label={cacheStore.GetString("OnHoldLabAction")}
                                />
                                <ButtonControl
                                    className="btn btn-primary push-right-5"
                                    id={labMessage.Model.LabFillerID}
                                    onClick={() => sessionStore.dispatch(LocalOperations.LabsUI.FileLab, { id: labMessage.Model.LabFillerID })}
                                    label={cacheStore.GetString("ClassifyLabel")}
                                />
                                {portalSyncButton}
                            </div>
                        </div>
                    )}
                </div>
                <div>
                    <div className="flex-container end" style={{ alignItems: "flex-end" }}>
                        <CheckboxControl
                            labelStyle={{ marginRight: "20px", paddingTop: "9px" }}
                            checked={this.props.Model.get("OnHoldCheckBox")}
                            operation={LocalOperations.LabsUI.ToggleHoldFileCheckBox}
                            label={cacheStore.GetString("OnHoldLabAction")}
                        />
                        {this.renderLowerRightButtons(
                            this.props.Model.get("OnHoldCheckBox")
                            , isBottomButtonDisabled
                        )}
                        {portalSyncButton &&
                            <div style={{ marginLeft: "5px" }}>{portalSyncButton}</div>
                        }
                    </div>
                </div>
            </div>
        );
    }

    private formatLabResults(labrequests: RequestDTO[], isAbnormal: boolean): JSX.Element[] {
        let labMessage = this.props.Model.get("LabMessage");

        const rows: JSX.Element[] = [];
        const rowsAbnormal: JSX.Element[] = [];

        let patient = this.props.Model.get("MylePatient");

        let previousCollectionDate = null;
        let previousSectionIdentifier = null;
        let previousObrContainerIsIrrelevant = false;
        let sortedLabRequest = labrequests.sortByProperty(x => x.SetId?.padLeft("0", 3));
        if (labMessage.Model.LabFillerID.toUpperCase().endsWith("SRALAB"))
            sortedLabRequest = labrequests.sortByPropertyDesc(x => x.SetId?.padLeft("0", 3));

        sortedLabRequest.forEach((requestElem, idx) => {
            let firstObxTitle = requestElem.ObservationsList.firstOrDefault()?.Text || "";
            let currentObrContainerIsIrrelevant = (firstObxTitle.startsWith(requestElem.USIT) || IsNullOrWhiteSpace(requestElem.USIT)) /* Obr Title empty or redondant */
                && requestElem.ObservationsList.length <= 1 /* Doesnt really represant a container */
                && !IsNullOrWhiteSpace(requestElem.SectionIdentifier); /* Can be associated to a parent container */

            let obrTitle = !currentObrContainerIsIrrelevant && requestElem.USIT || requestElem.SectionIdentifier || ""; // Add a bit of value by keeping parent container title instead (in case we are forced to keep the row)
            let hasSpecialCollectionDate = labMessage.Model.PrelevmentDateTime != requestElem.MaxObservationDatetime_CollectedDatetime && requestElem.MaxObservationDatetime_CollectedDatetime != null;

            let graphJsx = (patient || labMessage.Model.Patient.NAM) && requestElem.ObservationsList.some(obx => obx.OtherValidValues)
                ? (
                    <td
                        onClick={e => {
                            e.stopPropagation();
                            sessionStore.dispatch(LocalOperations.LabsUI.Graphs_ShowGraphs, { lab: requestElem, patientContactId: patient?.IndividualResponse.Contact_id, healthCardNumber: labMessage.Model.Patient.NAM });
                        }}
                    >
                        <a>
                            <img
                                alt="LabChart"
                                src={img_icon_labchart}
                                ref="imglabchart"
                            />
                        </a>
                    </td>
                )
                : (<td />);

            let isDifferentFromPreviousObr = previousCollectionDate != requestElem.MaxObservationDatetime_CollectedDatetime || previousSectionIdentifier != requestElem.SectionIdentifier;

            let skipObr = previousObrContainerIsIrrelevant && currentObrContainerIsIrrelevant && !isDifferentFromPreviousObr;
            if (!skipObr) {
                if (idx > 0)
                    rows.push(
                        <tr key={"_rq" + idx} className="noborder">
                            <td style={{ height: "20px" }}><span> </span></td>
                        </tr>
                    );

                rows.push(
                    <tr key={"rq" + idx} className="noborder">
                        <td colSpan={3}>
                            <span className="requestHeader">
                                {obrTitle || requestElem.SectionIdentifier || ""}
                                {hasSpecialCollectionDate &&
                                    <span className="requestHeaderDetail"> ({cacheStore.GetString("CollectedOnLabel")}: {moment(requestElem.MaxObservationDatetime_CollectedDatetime).format("YYYY/MM/DD HH:mm:ss A")}) </span>
                                }
                            </span>
                        </td>
                        <td>&nbsp; </td>
                        <td>&nbsp; </td>
                        {graphJsx}
                        <td>&nbsp; </td>
                    </tr>
                );
            }

            requestElem.ObservationsList.sortByProperty(x => x.SetId?.toString()?.padLeft("0", 3)).forEach((obx, idy) => {
                var actionHyperLink = (<a onClick={e => this.sendActionNoteOperation(LocalOperations.LabsUI.ActionNotesClick, obx, this.LabFillerID, e)} className="notes_dropdown_main">[<span className="bold-text red">{" + "}</span>]</a>);

                var notes_and_Actions = (
                    <td>{actionHyperLink}</td>
                );

                if (obx.MyleNotesList != null && obx.MyleNotesList.length > 0) {
                    var noteHyperLink = (<a onClick={e => this.sendActionNoteOperation(LocalOperations.LabsUI.NotesClick, obx, this.LabFillerID, e)} className="notes_dropdown_main pointer">[<strong className="red-text"> {obx.MyleNotesList.length} </strong>]</a>);

                    notes_and_Actions = (<td>{noteHyperLink} / {actionHyperLink}</td>);
                }

                // Actions
                var actionsDetails = (<td><span /></td>);
                if (obx.MyleActionsList != null && obx.MyleActionsList.length > 0) {
                    var comaSeperatedActions: JSX.Element[] = [];

                    obx.MyleActionsList.forEach((y, ida) => {
                        var colorClass = y.CompletedOn == null ? "pointer red-text" : "pointer green-text";

                        comaSeperatedActions.push(
                            <div key={ida} className="nowrap marginright5">
                                <span onClick={e => this.sendActionDetailOperation(y, this.LabFillerID, e)} className={colorClass}>{cacheStore.GetLookupById(y.ActionLookupId).GetValue() + ";\r\n "}</span>
                            </div>
                        );
                    });

                    actionsDetails = (<td>{comaSeperatedActions}</td>);
                }

                let obxGraphJsx = (patient || labMessage.Model.Patient.NAM) && obx.OtherValidValues
                    ? (
                        <td
                            onClick={e => {
                                e.stopPropagation();
                                sessionStore.dispatch(LocalOperations.LabsUI.Graphs_ShowGraphs, { observationtype: obx, patientContactId: patient?.IndividualResponse.Contact_id, healthCardNumber: labMessage.Model.Patient.NAM });
                            }}
                        >
                            <img
                                alt="LabChart"
                                className="pointer"
                                src={img_icon_labchart}
                                style={{ margin: "2px 0 0 3px" }}
                                ref="imglabchart"
                            />
                        </td>
                    )
                    : (<td />);

                let abnormality = obx.AbnormalityFlag || "";
                let status = obx.ResultStatus != "F" && obx.ResultStatus?.truncate(3) || "";
                let flags = abnormality;
                if (status)
                    flags = flags + `| ${status}`;

                // Observatio Row
                const valueToSendToTextControl = `${obx.Text ?? ""} ${obx.Text ? ":" : ""} ${obx.ValueText ?? ""} ${obx.UnitsIdentifier ?? ""}`;

                const observationIsAbnormal = !IsNullOrWhiteSpace(obx.AbnormalityFlag) && obx.AbnormalityFlag != "N";

                rows.push(
                    <tr
                        key={`request${idx}_observation${idy}`}
                        className={observationIsAbnormal ? "pointer red-row bold" : "pointer"}
                    >
                        {this.renderObservationColumn(
                            valueToSendToTextControl,
                            <span className="pointer padded">{obx.Text}</span>,
                            { "whiteSpace": "pre-wrap" }
                        )}
                        {this.renderObservationColumn(valueToSendToTextControl, obx.ValueText)}
                        {this.renderObservationColumn(valueToSendToTextControl, obx.UnitsIdentifier)}
                        {this.renderObservationColumn(valueToSendToTextControl, obx.ReferenceRange)}
                        {this.renderObservationColumn(valueToSendToTextControl, flags)}
                        {obxGraphJsx}
                        {/* TD */ notes_and_Actions /* TD */}
                        {/* TD */ actionsDetails /* TD */}
                    </tr>
                );
                if (observationIsAbnormal) {
                    rowsAbnormal.push(
                        <tr
                            key={`request${idx}_observation${idy}`}
                            className="pointer red-row bold"
                        >
                            {this.renderObservationColumn(
                                valueToSendToTextControl,
                                <span className="pointer padded">{obx.Text}</span>,
                                { "whiteSpace": "pre-wrap" }
                            )}
                            {this.renderObservationColumn(valueToSendToTextControl, obx.ValueText)}
                            {this.renderObservationColumn(valueToSendToTextControl, obx.UnitsIdentifier)}
                            {this.renderObservationColumn(valueToSendToTextControl, obx.ReferenceRange)}
                            {this.renderObservationColumn(valueToSendToTextControl, flags)}
                            {obxGraphJsx}
                            {/* TD */ notes_and_Actions /* TD */}
                            {/* TD */ actionsDetails /* TD */}
                        </tr>
                    );
                }

                obx.NotesList.forEach((observationNote, idyn) => {
                    if (observationNote.Comment == null || observationNote.Comment == "")
                        return;

                    // Observation Note Row
                    rows.push(
                        <tr
                            key={`request${idx}_observation${idy}_note${idyn}`}
                            className="noborder"
                            onClick={() => this.sendToTextControlArea(observationNote.Comment)}
                        >
                            <td style={{ "whiteSpace": "pre-wrap" }} className="pointer" colSpan={7}>
                                <span className="paddedx2" style={{ fontFamily: "monospace" }}>{observationNote.Comment.replace(/\n\n\n\n/g, "\n").replace(/\n\n/g, "\n")}</span>
                            </td>
                        </tr>
                    );
                });
            });

            requestElem.NotesList.forEach((requestNote, idxn) => {
                // Request Note Row
                rows.push(
                    <tr
                        key={`request${idx}_note${idxn}`}
                        className="noborder"
                        onClick={() => this.sendToTextControlArea(requestNote.Comment)}
                    >
                        <td style={{ "whiteSpace": "pre-wrap" }} colSpan={7}>
                            <span className="pointer padded" style={{ fontFamily: "monospace" }}>{requestNote.Comment.replace(/\n\n\n\n/g, "\n").replace(/\n\n/g, "\n")}</span>
                        </td>
                    </tr>
                );
            });

            previousSectionIdentifier = requestElem.SectionIdentifier;
            previousCollectionDate = requestElem.MaxObservationDatetime_CollectedDatetime;
            previousObrContainerIsIrrelevant = currentObrContainerIsIrrelevant;
        });

        return isAbnormal ? rowsAbnormal : rows;
    }

    private renderLowerRightButtons(isOnHold: boolean, isOpenAproveBottomDisabled: boolean): JSX.Element {
        let OpenButton, ApprouveButton;
        if (isOnHold) {
            ApprouveButton = (
                <ButtonControl
                    className="btn btn-primary"
                    disabled={isOpenAproveBottomDisabled}
                    onClick={() => sessionStore.dispatch(LocalOperations.LabsUI.PutOnHold_ApprouveEncounter)}
                    label={cacheStore.GetString("OnHoldLabAction") + " + " + cacheStore.GetString("ApproveLabel")}
                />
            );
            OpenButton = (
                <ButtonControl
                    className="btn btn-primary push-right-5"
                    disabled={isOpenAproveBottomDisabled}
                    onClick={() => sessionStore.dispatch(LocalOperations.LabsUI.PutOnHold_SaveAndOpenEncounter)}
                    label={cacheStore.GetString("OnHoldLabAction") + " + " + cacheStore.GetString("OpenLabel")}
                />
            );
        }
        else {
            OpenButton = (
                <ButtonControl
                    className="btn btn-primary push-right-5"
                    disabled={isOpenAproveBottomDisabled}
                    onClick={() => sessionStore.dispatch(LocalOperations.LabsUI.File_SaveAndOpenEncounter)}
                    label={cacheStore.GetString("ClassifyLabel") + " + " + cacheStore.GetString("OpenLabel")}
                />
            );

            const approveButtonLabel = cacheStore.GetString("ClassifyLabel") + " + " + cacheStore.GetString("ApproveLabel");

            ApprouveButton = (
                <ButtonControl
                    className="btn btn-primary"
                    disabled={isOpenAproveBottomDisabled}
                    onClick={() => {
                        LinkingPopupService
                            .promptForAdditionalSignOffInformation(
                                this.props.Model.get("MylePatient").IndividualResponse.Contact_id,
                                sessionStore.getCurrentEmployee().getActiveSite().Model.Id,
                                null,
                                null
                            )
                            .then(info => {
                                sessionStore.dispatch(LocalOperations.LabsUI.File_ApprouveEncounter, { additionalSignOffInformation: info });
                            })
                            .catch(p => p && console.error(p)); // prompt cancelled
                    }}
                    label={approveButtonLabel}
                />
            );
        }

        return (
            <div>
                {OpenButton}
                {ApprouveButton}
            </div>
        );
    }

    private renderObservationColumn(valueToSendToTextControl: string, value: React.ReactNode, style: React.CSSProperties = {}): JSX.Element {
        return (
            <td
                style={style}
                onClick={() => this.sendToTextControlArea(valueToSendToTextControl)}
            >
                {value}
            </td>
        );
    }

    private formatEncapsolatedData(labMessage: LabMessage): JSX.Element[] {
        var rows: JSX.Element[] = [];

        if (labMessage.Model.LinkedEmptyEncapsulatedData != null) {
            labMessage.Model.LinkedEmptyEncapsulatedData.forEach((Attachment, idx) => {
                var filename = cacheStore.GetString("AttachmentLabel");
                if (Attachment.FileName != null && Attachment.FileName.length > 0)
                    filename = Attachment.FileName;

                filename += "-" + (idx + 1) + "." + Attachment.Extension;

                var AttachementJSX = (
                    <HyperLinkControl
                        key={"HyperlinkAttachment_" + idx}
                        text={filename}
                        marginRight="15px"
                        id={Attachment.Guid}
                        operation={LocalOperations.LabsUI.OpenAttachment}
                    />
                );
                rows.push(AttachementJSX);
            });
        }
        return rows;
    }

    private formatLabHeaderStatus(labActions: LabActionTaken[]): JSX.Element[] {
        const rows: JSX.Element[] = [];

        labActions.forEach((labAction, idx) => {
            const rowClass = (idx == labActions.length - 1) ? "row" : "row clear-margin-bottom"; // If last row keep margintie

            const actionResolved = labAction.Action == "Submit"
                ? cacheStore.GetString("SubmittedbyLabel")
                : cacheStore.GetString("FilebyLabel");

            rows.push(
                <div key={idx} className={rowClass}>
                    <div className="col-lg-12 col-md-12 col-sm-12 text-center">
                        <strong><span style={{ color: "#ff0000" }} className="red-text">{actionResolved} {labAction.Doctor} ({labAction.DoctorLicence}) - {moment(labAction.ActionDateTime).format("YYYY/MM/DD HH:mm:ss A")}</span></strong>
                    </div>
                </div>
            );
        });

        return rows;
    }

    private getColumn(id: number, a: string, b: string): JSX.Element {
        return (
            <tr key={id}>
                <td className="c1">
                    <span>{a}</span>
                </td>
                <td className="c2">
                    <strong>{b}</strong>
                </td>
            </tr>
        );
    }

    private toggleAdditionalInfo(): void {
        LocalEvent.Dispatch(LocalOperations.LabsUI.ToggleAdditionalInfo);
    }

    private ToggleAbnormalAndCriticalInfo(): void {
        LocalEvent.Dispatch(LocalOperations.LabsUI.ToggleAbnormalAndCriticalInfo);
    }

    private expandLeftMenu(): void {
        LocalEvent.Dispatch(LocalOperations.LabsUI.ExpandLeftMenu);
    }

    private collapseLeftMenu(): void {
        this.hideClickableText();
        LocalEvent.Dispatch(LocalOperations.LabsUI.CollapseLeftMenu);
    }

    private globalNotesClick(labFillerId: string, notes: MyleNoteDTO[], e: React.MouseEvent): void {
        e.preventDefault();
        e.stopPropagation();
        const operation = new LocalEvent(LocalOperations.LabsUI.NotesClick);
        operation.AddItem("labFillerId", labFillerId);
        operation.AddItem("notes", notes);
        operation.AddItem("actions", []);
        operation.AddItem("event", e);
        operation.AddItem("isGlobal", true);
        LocalEventDispatcher.dispatch(operation);
    }

    private globalResultDetailClick(
        labFillerId: string,
        notes: MyleNoteDTO[],
        detail: string,
        event: React.MouseEvent
    ): void {
        event.preventDefault();
        event.stopPropagation();

        sessionStore.dispatch(LocalOperations.LabsUI.ResultDetailClick, {
            labFillerId,
            notes,
            actions: [],
            event,
            isGlobal: true,
            detail,
            isManualLabEntry: this.props.Model.get("LabMessage").Model.IsManualLabEntry
        });
    }

    private sendActionNoteOperation(clickOperationId: LocalOperations.LabsUI, obx: ObservationDTO, labFillerId: string, e: React.MouseEvent): void {
        e.preventDefault();
        e.stopPropagation();
        const operation = new LocalEvent(clickOperationId);
        operation.AddItem("labFillerId", labFillerId);
        operation.AddItem("observation", obx);
        operation.AddItem("notes", obx.MyleNotesList);
        operation.AddItem("actions", obx.MyleActionsList);
        operation.AddItem("event", e);
        LocalEventDispatcher.dispatch(operation);
    }

    private sendActionDetailOperation(actionItem: MyleActionDTO, labFillerId: string, e: React.MouseEvent): void {
        e.stopPropagation();
        const operation = new LocalEvent(LocalOperations.LabsUI.ActionDetails_Click);
        operation.AddItem("labFillerId", labFillerId);
        operation.AddItem("actionItem", actionItem);
        operation.AddItem("event", e);
        LocalEventDispatcher.dispatch(operation);
    }

    private sendToTextControlArea(value: string): void {
        const operation = new LocalEvent(LocalOperations.Misc.ClickableTextControlAppend);
        operation.AddItem("value", value.replace(/\n\n\n\n/g, "\n").replace(/\n\n/g, "\n"));
        LocalEventDispatcher.dispatch(operation);

        this.FillReasonForVisit();
    }

    private FillReasonForVisit(): void {
        const labfillerId = this.props.Model.get("LabMessage").Model.LabFillerID;
        const cleanLabFillerId = this.getlabFillerId(labfillerId);

        // Fill Raison of visit if its empty
        const operation = new LocalEvent(LocalOperations.Misc.ClickableTextControlAppend);
        operation.AddItem("value", cacheStore.GetString("ResultReviewText"));
        operation.AddItem("SpecificTextControlId", "lab_note_reason" + cleanLabFillerId);
        operation.AddItem("ApplyOnlyIfFieldIsNullOrEmpty", true);

        LocalEventDispatcher.dispatch(operation);
    }

    private showClickableText(clickableText: string): void {
        const operation = new LocalEvent(LocalOperations.LabsUI.ShowClickableText);
        operation.AddItem("value", clickableText);
        LocalEventDispatcher.dispatch(operation);
    }

    private hideClickableText(): void {
        const operation = new LocalEvent(LocalOperations.LabsUI.HideClickableText);
        LocalEventDispatcher.dispatch(operation);
    }

    private PortalStatusClicked(msg: LabMessage): void {
        const item = new LabMessageTableItem(msg);
        if (item != null) {
            const operation = new LocalEvent(LocalOperations.LabsUI.PortalStatusClicked);
            operation.AddItem("lab", item.Model);
            if (this.myleInfoPatient && this.myleInfoPatient.IndividualResponse) {
                operation.AddItem("registrationStatus", this.myleInfoPatient.IndividualResponse.PortalSyncState);
                operation.AddItem("patientContactId", this.myleInfoPatient.IndividualResponse.Contact_id);
            }

            LocalEventDispatcher.dispatch(operation);
        }
    }

    private onNewTaskClick(): void {
        sessionStore.dispatch(
            LocalOperations.SendTaskOrMessageModel.Show_TaskOrMessage_Popup,
            {
                id: this.LabFillerID,
                target: MessageOrTaskContext.Labs,
                item: this.myleInfoPatient,
                lab: this.props.Model.get("LabMessage")
            }
        );
    }

    private onTaskTrailClick(e: React.MouseEvent): void {
        sessionStore.dispatch(
            LocalOperations.LabsUI.ShowAttachedCommunicationsPopup,
            {
                eventY: e.clientY,
                eventX: e.clientX
            }
        );
    }

    private onAttachedCommunicationClick(attachedCommunication: AttachedCommunication): void {
        sessionStore.dispatch(
            LocalOperations.LabsUI.RedirectAndCloseAttachedCommunicationPopup,
            {
                redirect: () => {
                    sessionStore.dispatch(
                        LocalOperations.Misc.RedirectToTab,
                        {
                            value: attachedCommunication.PatientContactId,
                            currentLocation: RouterHistory.location,
                            taskId: attachedCommunication.IndividualMessageId,
                            target: RedirectTo.Patient
                        }
                    );
                }
            }
        );
    }

    private onAttachedCommunicationClose(): void {
        sessionStore.dispatch(LocalOperations.LabsUI.HideAttachedCommunicationsPopup);
    }

    private showExternalCommunicationPopup(show: boolean = true): void {
        sessionStore.dispatch(
            LocalOperations.LabsUI.ShowExternalCommunicationPopup,
            {
                show: show
            }
        );
    }

    private openSecondScreen(): void {
        const patientId = this.props.Model.get("MylePatient").IndividualResponse.Contact_id;
        SecondScreenHelper.openWindow({ patientId });
    }

    private updateSecondScreen(closePatient: boolean = false): void {
        if (SecondScreenNoteService.getInstance().isChildWindowOpen()) {
            if (closePatient) {
                SecondScreenNoteService.getInstance().updateModel(new SecondScreenNoteData({ patientId: null }));
            }
            else if (this.props.Model?.get("MylePatient")?.IndividualResponse?.Contact_id) {
                SecondScreenNoteService.getInstance().updateModel(new SecondScreenNoteData({ patientId: this.props.Model?.get("MylePatient").IndividualResponse.Contact_id }));
            }
        }
    }

    private toggleMedicalSummaryPanel(): void {
        this.mutateState(s => s.isMedicalSummaryPanelOpen = !s.isMedicalSummaryPanelOpen, () => {
            MedicalSummaryPanelAction.updateMedicalSummaryPanelSettingsWithContext(
                cacheStore.UserSettings?.LabViewMedicalSummaryPanelSettings,
                this.state.isMedicalSummaryPanelOpen,
                MedicalSummaryPanelContext.ResultsTab
            );
        });
    }

    private getlabFillerId(labFillerId: string): string {
        return this.myleHelper.RemoveDiacritics(MyleHelper.RemoveSpaces(labFillerId)).replaceAll("/", "").replaceAll(".", "");
    }

    private toggleCreatePatientPopupClick(): void {
        sessionStore.dispatch(LocalOperations.LabsUI.ToggleCreatePatientPopup);
    }

    private createNewPatient(patient: IPatientCreationModel): void {
        sessionStore.dispatch(LocalOperations.LabsUI.CreateNewPatientAndLinkResult, { patient });
    }

    private renderCreatePatientPopup(): JSX.Element {
        return (
            <CreatePatientPopup
                patientCreationModel={labsStore.LabModel.get("PatientCreationModel")}
                onSave={patient => this.createNewPatient(patient)}
                onCancel={() => this.toggleCreatePatientPopupClick()}
                customOkButtonLabel={cacheStore.GetString("LinkLabel")}
                customTitle={cacheStore.GetString("LinkPatientLabel").toTitleCase()}
                displayCreatePatientLabel={true}
            />
        );
    }

    private isManualLabInbox(): boolean {
        return labsStore.LabsInboxModel.get("CurrentInbox") === LabInboxes.ManualResults;
    }
}

export { ILabViewProps, LabView, LabViewState };
